/**
 * Curated synonym expansions for the directory free-text search.
 *
 * When a query mentions any of a rule's `triggers`, the search ALSO matches the
 * rule's `expand` phrases — so plain-language queries reach the seed specialty
 * vocabulary. Example: "dependant visa" (or just "spouse") should find pros
 * tagged "Dependency Visas" or "Family Visas", even though those strings share
 * no substring with the query.
 *
 * `expand` entries are deliberately full "<x> visa" PHRASES, never bare words.
 * The expansion is matched as a substring against a pro's specialties, so a
 * bare "family" would also hit unrelated specialties like "Family Law" or
 * "Family Office"; "family visa" cannot. Only the user's own typed query is
 * left unscoped (if someone literally searches "family", matching family-law
 * pros is reasonable — that's their word, not our inference).
 *
 * Keep rules small and high-signal. Synonyms are applied to the STRUCTURED
 * specialties match only (see getProfessionalsForSearch), never to free-text
 * bio/name matching.
 */
const SYNONYM_EXPANSIONS: readonly {
  triggers: readonly string[];
  expand: readonly string[];
}[] = [
  {
    // Spouse / dependant / family visas. Seed vocabulary uses "Dependency
    // Visas" and "Family Visas"; users type "spouse" / "dependant" / "family".
    triggers: ["dependant", "dependent", "dependency", "spouse", "family"],
    expand: [
      "dependant visa",
      "dependent visa",
      "dependency visa",
      "family visa",
      "spouse visa",
    ],
  },
  {
    // Mergers and acquisitions. Firms tag it "M&A", which shares no substring
    // with "merge" or "acquisition", so those searches found nobody (owner,
    // 2026-09-22). French and Vietnamese triggers too: the search runs in all
    // three languages. "acquisition" is expanded as the full "m&a" tag only,
    // never bare, so a land or property acquisition specialty is not pulled in.
    triggers: [
      "merge", "merger", "mergers", "merging", "acquisition", "acquisitions", "acquire",
      "takeover", "m&a", "m & a", "fusion", "fusions", "fusion-acquisition",
      "sáp nhập", "mua bán sáp nhập", "sap nhap",
    ],
    expand: ["m&a", "mergers", "mergers and acquisitions"],
  },
];

function normalize(s: string): string {
  return s.toLowerCase().trim();
}

/**
 * Expand a free-text query into the set of substring terms to match against
 * specialties. Always includes the original (normalized) query; for any rule
 * whose triggers the query mentions, adds that rule's `expand` phrases.
 *
 * Single-word triggers match on token boundaries (so "independent" does NOT
 * trigger the "dependent" rule); multi-word triggers match as substrings.
 */
export function expandQueryTerms(raw: string): string[] {
  const q = normalize(raw);
  if (!q) return [];
  const terms = new Set<string>([q]);
  const tokens = new Set(q.split(/\s+/).filter(Boolean));
  for (const { triggers, expand } of SYNONYM_EXPANSIONS) {
    const mentioned = triggers.some((t) =>
      t.includes(" ") ? q.includes(t) : tokens.has(t),
    );
    if (mentioned) for (const phrase of expand) terms.add(phrase);
  }
  return [...terms];
}

/**
 * The words that should SUGGEST a specialty as you type (search autocomplete):
 * the triggers of every rule whose expansion names that specialty. "M&A" gets
 * "merge", "merger", "fusion", "sáp nhập"…, so typing "merg" offers it.
 * Same rules as the search itself, so the dropdown and the results agree.
 */
export function synonymAliasesFor(specialty: string): string[] {
  const label = normalize(specialty);
  const out = new Set<string>();
  for (const { triggers, expand } of SYNONYM_EXPANSIONS) {
    if (expand.some((phrase) => label.includes(phrase))) for (const t of triggers) out.add(t);
  }
  return [...out];
}
